import { useCallback, useEffect, useId, useState } from 'react';
import styled from '@emotion/styled';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import SkipButton from 'src/components/AzarOnly/Login/SkipButton';
import { Description } from 'src/components/AzarOnly/Login/Steps/styles';
import useRegister from 'src/components/AzarOnly/Login/useRegister';
import Button from 'src/components/Button';
import { ModalContent, ModalFooter as ModalFooterBase, ModalHeader as ModalHeaderBase, ModalTitle, ModalTitleContainer, ModalTitleDescription } from 'src/components/ModalLayout';
import useFileHandler from 'src/hooks/useFileHandler';
import { loginModalHeaderAtom } from 'src/stores/phoneNumberLogin/atoms';
import { FALLBACK_SIGNUP_SETTINGS, profileImageStateAtom, signUpSettingsLoadableAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
const FormWrap = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
  height: 100%;
`;
const ProfileImageContainer = styled.label`
  position: relative;
  width: 160px;
  height: 236px;
  border-radius: 16px;
  background: ${({
  theme
}) => theme.color.surface4};
  overflow: hidden;
  background-size: cover;
  background-position: center;
`;
const HiddenInput = styled.input`
  display: none;
`;
const DefaultProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const ProfileImageButton = styled.div`
  width: 32px;
  height: 32px;
  position: absolute;
  ${({
  theme
}) => theme.isRtl ? 'left' : 'right'}: 8px;
  bottom: 8px;
  border-radius: 16px;
  background-color: ${({
  theme
}) => theme.color.surfaceDeep};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const Icon = styled.img`
  width: 16px;
  height: 16px;
`;
const DesktopSkipButton = styled(SkipButton)`
  ${({
  theme
}) => theme.screenSize.mobile} {
    display: none;
  }
`;
const MobileSkipButton = styled(SkipButton)`
  display: none;

  ${({
  theme
}) => theme.screenSize.mobile} {
    display: block;
    width: fit-content;
    padding: 0;
  }
`;
const ModalHeader = styled(ModalHeaderBase)`
  flex-direction: row;
  justify-content: flex-end;
`;
const ModalFooter = styled(ModalFooterBase)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;
const ProfileImageStep = () => {
  const {
    tryRegister
  } = useRegister();
  const {
    t
  } = useTranslation();
  const [profileImageState, setProfileImageState] = useAtom(profileImageStateAtom);
  const [previewUrl, setPreviewUrl] = useState<string | null>(profileImageState.status === 'draft' ? URL.createObjectURL(profileImageState.file) : null);
  const formId = useId();
  const setLoginModalHeader = useSetAtom(loginModalHeaderAtom);
  const signUpSettingsLoadable = useAtomValue(signUpSettingsLoadableAtom);
  const signUpSettings = signUpSettingsLoadable.state === 'hasData' ? signUpSettingsLoadable.data : FALLBACK_SIGNUP_SETTINGS;
  const showSkipButton = signUpSettings.profileImageSettings?.skipEnabled === true;
  const handleFileUpload = useCallback((file: File) => {
    setProfileImageState({
      status: 'draft',
      file
    });

    // 미리보기 URL 생성
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }, [setProfileImageState]);
  const handleFileChange = useFileHandler(handleFileUpload);
  const handleNext = useCallback(async () => {
    if ('file' in profileImageState) {
      setProfileImageState({
        status: 'selected',
        file: profileImageState.file
      });
    }
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_NEXT_PROFILEIMAGE,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'profileimage',
        target: 'next'
      }
    });
    await tryRegister();
  }, [tryRegister, profileImageState, setProfileImageState]);
  const handleSkip = useCallback(async () => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_SKIP_PROFILEIMAGE,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'profileimage',
        target: 'skip'
      }
    });
    setProfileImageState({
      status: 'skipped'
    });
    await tryRegister({});
  }, [tryRegister, setProfileImageState]);
  useEffect(() => {
    // 컴포넌트 언마운트 시 미리보기 URL 정리
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
  useEffect(() => {
    if (showSkipButton) {
      setLoginModalHeader({
        component: <ModalHeader>
            <MobileSkipButton onSkip={handleSkip} />
          </ModalHeader>
      });
    }
    return () => {
      setLoginModalHeader(undefined);
    };
  }, [handleSkip, setLoginModalHeader, showSkipButton]);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__IMPRESSION_PROFILEIMAGE,
      eventParams: {
        action_category: 'impression',
        tab: 'signup',
        page: 'profileimage',
        target: 'profileimage'
      }
    });
  }, []);
  useEffect(() => {
    if (profileImageState.status === 'selected') {
      setProfileImageState({
        status: 'draft',
        file: profileImageState.file
      });
    }
  }, []);
  return <>
      <ModalTitleContainer data-sentry-element="ModalTitleContainer" data-sentry-source-file="ProfileImageStep.tsx">
        <ModalTitle data-testid='LoginModal-ProfileImageStep' data-sentry-element="ModalTitle" data-sentry-source-file="ProfileImageStep.tsx">
          {t('signup__info_profileimage_title')}
        </ModalTitle>
        <ModalTitleDescription data-sentry-element="ModalTitleDescription" data-sentry-source-file="ProfileImageStep.tsx">
          <Description data-sentry-element="Description" data-sentry-source-file="ProfileImageStep.tsx">{t('signup__info_profileimage_desc')}</Description>
        </ModalTitleDescription>
      </ModalTitleContainer>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="ProfileImageStep.tsx">
        <FormWrap id={formId} onSubmit={e => {
        e.preventDefault();
        handleNext();
      }} data-sentry-element="FormWrap" data-sentry-source-file="ProfileImageStep.tsx">
          <ProfileImageContainer htmlFor='profile-image-upload' style={{
          backgroundImage: previewUrl ? `url(${previewUrl})` : undefined
        }} data-sentry-element="ProfileImageContainer" data-sentry-source-file="ProfileImageStep.tsx">
            {!previewUrl && <DefaultProfileImage src='/images/profile/ProfileNull.png' alt='Default Profile' />}

            <ProfileImageButton data-sentry-element="ProfileImageButton" data-sentry-source-file="ProfileImageStep.tsx">
              <Icon src='/images/icons/icEdit.svg' alt='Edit First Profile Card' data-sentry-element="Icon" data-sentry-source-file="ProfileImageStep.tsx" />
            </ProfileImageButton>
          </ProfileImageContainer>

          <HiddenInput id='profile-image-upload' type='file' accept='image/*' onChange={handleFileChange} data-sentry-element="HiddenInput" data-sentry-source-file="ProfileImageStep.tsx" />
        </FormWrap>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="ProfileImageStep.tsx">
        <Button id='sign-up-profile-image-next' onClick={handleNext} disabled={profileImageState.status !== 'draft'} data-testid='ProfileImageStep-button-next' data-sentry-element="Button" data-sentry-source-file="ProfileImageStep.tsx">
          {t('SIGNUP__NEXT')}
        </Button>
        {showSkipButton && <DesktopSkipButton onSkip={handleSkip} data-testid='ProfileImageStep-button-skip' />}
      </ModalFooter>
    </>;
};
export default ProfileImageStep;